/**
 * Поиск похожих референсов по фото: типы ответа `/api/library/similar` и чистые
 * помощники выдачи. Вызов ручки — `searchSimilar` в `lib/library.ts`: здесь только
 * `import type`, модуль напрямую грузит `node --test`.
 */
import type { ReferenceItem } from "./library";

export type ViewTag = "front" | "back";
export type Hit = ReferenceItem & {
  score: number;
  view?: ViewTag | null;
  /** Поиск по платью: фото платья (`own_photo.id`) с лучшим score находки. */
  photo_id?: number | null;
  /** Поиск по платью: score по каждому фото, чей топ включил находку (ключ — `own_photo.id` строкой). */
  photo_scores?: Record<string, number> | null;
};
export type SimilarResult = {
  hits: Hit[];
  missing: number;
  total: number;
  query_views?: ViewTag[] | null;
  /** Поиск по описанию: введённый текст и английский, который искали. */
  query_text?: string | null;
  query_en?: string | null;
};
/** Выдача в кэше react-query: строки под `items`, чтобы обозреватель патчил их как любой срез. */
export type SimilarView = {
  items: Hit[];
  missing: number;
  total: number;
  query_views?: ViewTag[] | null;
  query_text?: string | null;
  query_en?: string | null;
};

/** `0.934` → `"93 %"`. */
export const scoreLabel = (score: number): string => `${Math.round(score * 100)} %`;

/** Ответ ручки → выдача для кэша. Порядок `hits` — серверный (тот же ракурс первым, внутри по score): не сортируем. */
export const similarView = (got: SimilarResult): SimilarView => ({
  items: got.hits,
  missing: got.missing,
  total: got.total,
  query_views: got.query_views ?? null,
  query_text: got.query_text ?? null,
  query_en: got.query_en ?? null,
});

/** Подпись к выдаче по описанию: «по описанию «замок» → a bride in a castle»; не текст — пусто. */
export function textCaption(view: Pick<SimilarView, "query_text" | "query_en">): string {
  const t = view.query_text;
  if (t === null || t === undefined) return "";
  const en = view.query_en;
  const norm = (x: string) => x.trim().split(/\s+/).join(" ").toLowerCase();
  return en && norm(en) !== norm(t) ? `по описанию «${t}» → ${en}` : `по описанию «${t}»`;
}

/** Платный перевод описания не повторяем: `TRANSLATE_DOWN` мог уйти к провайдеру и списаться; остальное — как `retryQuery`. */
export function retrySimilar(count: number, error: unknown): boolean {
  const items = (error as { items?: { code?: string }[] } | null)?.items;
  if (items?.some((i) => i.code === "TRANSLATE_DOWN")) return false;
  const status = (error as { status?: unknown } | null)?.status;
  return !(typeof status === "number" && status >= 400 && status < 500) && count < 2;
}

/** Процент сходства на плитке: у выдачи по описанию его нет (оценки разных шкал). */
export const showsPercent = (view: Pick<SimilarView, "query_text">): boolean =>
  view.query_text === null || view.query_text === undefined;

/** Подпись к выдаче, когда все отмеченные фото смотрят с одной стороны; иначе пусто. */
export function viewCaption(queryViews: readonly string[] | null | undefined): string {
  if (queryViews?.length !== 1) return "";
  if (queryViews[0] === "back") return "вид сзади — сначала такой же ракурс";
  if (queryViews[0] === "front") return "вид спереди — сначала такой же ракурс";
  return "";
}

/** Ключ кэша поиска по платью: `dress:<id>` или с набором отмеченных фото (порядок не важен). */
export const similarKey = (dressId: number, photoIds: readonly number[]): string =>
  photoIds.length === 0 ? `dress:${dressId}` : `dress:${dressId}:${[...photoIds].sort((a, b) => a - b).join(",")}`;

/** Источник окна выбора → `library_id` поиска: корзину и «все» ручка ищет по всем библиотекам. */
export const searchScope = (source: string): number | null =>
  source === "basket" || source === "all" ? null : Number(source);

/** Показанный поиск окна выбора: платье, его отмеченные фото и область (`null` — все библиотеки). */
export type SentSearch = { dressId: number; photoIds: number[]; scope: number | null };

/** Сменили источник при показанном поиске (№434) — тот же поиск в новой области; поиска нет — нечего перезапускать. */
export const rescope = (sent: SentSearch | null, source: string): SentSearch | null =>
  sent === null || sent.scope === searchScope(source) ? sent : { ...sent, scope: searchScope(source) };

/**
 * Один выбор (радио, №435): клик заменяет выбранное, повторный клик ничего не снимает; клик по текущему
 * (`current` — то, что уже стоит) возвращает к нему — выбирать нечего, отметок нет.
 */
export const pickOne = (id: number, current: readonly number[]): ReadonlySet<number> =>
  current.includes(id) ? new Set() : new Set([id]);

export const scoreMap = (hits: readonly { id: number; score: number }[]): Map<number, number> =>
  new Map(hits.map((h) => [h.id, h.score]));

/**
 * Что панель отдаёт экрану: `null` — выдачи нет (источника нет или ответа ещё не было),
 * `"keep"` — идёт новый запрос, прежнюю выдачу не трогаем, иначе — выдача.
 * Placeholder без источника (после сброса) — не «ждём ответ», а сброс.
 */
export function reportFor<T>(source: string | null, data: T | undefined, isPlaceholderData: boolean): T | null | "keep" {
  if (source === null) return null;
  if (isPlaceholderData) return "keep";
  return data ?? null;
}

/**
 * Запрос панели: картинка (`n` — свой номер на каждую, даже на ту же ещё раз), платье или описание.
 * `F` — что лежит при картинке (в панели — `File`), тестам хватает строки.
 */
export type SearchChoice<F> = { kind: "file"; n: number; file: F; url: string } | { kind: "dress"; id: number }
  | { kind: "text"; text: string };
export type Submitted<F> = { choice: SearchChoice<F>; scope: number | null };
/**
 * Выбранное и отправленное — порознь: выбор поиск не запускает, его запускает «Найти»,
 * а прежняя выдача (по `submitted`) видна, пока не нажали «Найти» снова.
 */
export type SearchState<F> = { chosen: SearchChoice<F> | null; submitted: Submitted<F> | null };
export type SearchAction<F> =
  | { type: "choose"; choice: SearchChoice<F> }
  /** «убрать» у превью: выбор пуст, выдача остаётся. */
  | { type: "clear" }
  | { type: "submit"; scope: number | null }
  /** «сбросить» и закрытие панели: ни выбора, ни выдачи. */
  | { type: "reset" }
  /** Ссылка `?similar_dress=` / `?similar_text=` — выбрать и сразу искать. */
  | { type: "deeplink"; dressId?: number; text?: string; scope: number | null };

export const EMPTY_SEARCH: SearchState<never> = { chosen: null, submitted: null };

/** Начальное состояние: платье или описание из адреса — сразу отправленный запрос. */
export function initialSearch<F>(dressId: number | null, scope: number | null, text?: string | null): SearchState<F> {
  const choice: SearchChoice<F> | null = text
    ? { kind: "text", text }
    : dressId !== null
      ? { kind: "dress", id: dressId }
      : null;
  if (choice === null) return EMPTY_SEARCH;
  return { chosen: choice, submitted: { choice, scope } };
}

export const sameChoice = <F>(a: SearchChoice<F> | null, b: SearchChoice<F> | null): boolean =>
  a === null || b === null
    ? a === b
    : a.kind === "file"
      ? b.kind === "file" && a.n === b.n
      : a.kind === "dress"
        ? b.kind === "dress" && a.id === b.id
        : b.kind === "text" && a.text === b.text;

/** Ключ источника для кэша: `file:3` / `dress:73` / `text:замок`; `null` — не отправляли. */
export const sourceOf = <F>(submitted: Submitted<F> | null): string | null =>
  submitted === null
    ? null
    : submitted.choice.kind === "file"
      ? `file:${submitted.choice.n}`
      : submitted.choice.kind === "dress"
        ? `dress:${submitted.choice.id}`
        : `text:${submitted.choice.text}`;

/** Что сделает «Найти»: `none` — нечего искать, `same` — тот же запрос (перезапрос), `new` — новый. */
export function submitKind<F>(state: SearchState<F>, scope: number | null): "none" | "same" | "new" {
  if (state.chosen === null) return "none";
  const s = state.submitted;
  return s !== null && s.scope === scope && sameChoice(s.choice, state.chosen) ? "same" : "new";
}

export function searchReducer<F>(state: SearchState<F>, action: SearchAction<F>): SearchState<F> {
  switch (action.type) {
    case "choose":
      return { ...state, chosen: action.choice };
    case "clear":
      return state.chosen === null ? state : { ...state, chosen: null };
    case "submit":
      if (submitKind(state, action.scope) !== "new" || state.chosen === null) return state;
      return { ...state, submitted: { choice: state.chosen, scope: action.scope } };
    case "reset":
      return state.chosen === null && state.submitted === null ? state : EMPTY_SEARCH;
    case "deeplink":
      return initialSearch(action.dressId ?? null, action.scope, action.text);
  }
}
